var _div, _div2;
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useFeatures } from '@atc/features';
import { IconButton, LinkIcon, SideBarToModal, Tab, Tabs } from 'reaxl';
import { SaveGlyph } from 'reaxl-molecules';
import { useBrand } from '@atc/brand';
import ShareEmail from './ShareEmail';
import ShareText from './ShareText';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const sideBarOptions = {
  fullWidth: true
};
const modalOptions = {
  scrollPanel: false
};

/**
 * Utility function to handle SMS triggering on mobile devices when only text sharing is available.
 * Moved outside component for performance optimization and reusability
 * @param {string} message - The SMS message content
 * @param {function} callback - Optional callback to execute after SMS trigger
 */
const handleSmsOnMobileWhenTextOnly = (message, callback) => {
  window.location.href = `sms:?&body=${encodeURIComponent(message)}`;
  if (typeof callback === 'function') {
    callback();
  }
};

/**
 *  Share email or text with a share icon that opens a modal with email and text functionality
 *
 */
function ShareContent(_ref) {
  let {
    buttonText = 'Share',
    emailProps = {},
    glyphColor,
    isMobile = false,
    isStaticContent = false,
    onClick = () => {},
    resetEmailFriend = () => {},
    resetTextFriend = () => {},
    textProps = {
      message: '',
      url: '',
      onTabClick: () => {}
    },
    title = 'Share this car',
    uiContext = 'stacked',
    ...rest
  } = _ref;
  const {
    share: [, {
      show_email_tab: showEmailTab = true
    }]
  } = useFeatures(['share']);
  const isStacked = uiContext === 'stacked';
  const isInline = uiContext === 'inline';
  const [isOpen, setModalOpen] = useState(false);
  const [smsTriggered, setSmsTriggered] = useState(false);
  const {
    brand
  } = useBrand();
  const onClickOpen = event => {
    setModalOpen(true);
    onClick(event);
  };
  const onClickClose = () => {
    setModalOpen(false);
    resetEmailFriend();
    resetTextFriend();
  };
  const onTabSelect = key => {
    if (key === 2 && isMobile) {
      window.location.href = `sms:?&body=${textProps.message}`;
      textProps.onTabClick();
      onClickClose();
    }
  };
  let content;
  const hasTextTab = textProps.message;
  const callBack = textProps.callback;
  useEffect(() => {
    if (isMobile && !smsTriggered && !showEmailTab && hasTextTab) {
      setSmsTriggered(true);
      handleSmsOnMobileWhenTextOnly(hasTextTab, callBack);
    }
  }, [isMobile, smsTriggered, showEmailTab, hasTextTab, callBack]);
  if (!showEmailTab && !hasTextTab) {
    content = _div || (_div = /*#__PURE__*/_jsx("div", {
      children: "No sharing options available"
    }));
  } else if (showEmailTab && !hasTextTab) {
    content = /*#__PURE__*/_jsx(ShareEmail, {
      ...emailProps
    });
  } else if (!showEmailTab && hasTextTab) {
    content = isMobile ? _div2 || (_div2 = /*#__PURE__*/_jsx("div", {})) : /*#__PURE__*/_jsx(ShareText, {
      ...textProps
    });
  } else {
    // Both tabs available
    content = /*#__PURE__*/_jsxs(Tabs, {
      alignment: "left",
      defaultActiveKey: 1,
      id: "share-content-tabs",
      onSelect: onTabSelect,
      children: [/*#__PURE__*/_jsx(Tab, {
        eventKey: 1,
        title: "Email",
        className: "margin-top-5",
        children: /*#__PURE__*/_jsx(ShareEmail, {
          ...emailProps
        })
      }), /*#__PURE__*/_jsx(Tab, {
        eventKey: 2,
        title: "Text",
        className: "margin-top-5",
        children: isMobile ? null : /*#__PURE__*/_jsx(ShareText, {
          ...textProps
        })
      })]
    });
  }
  if (isStaticContent) return content;
  let modalTrigger;
  switch (uiContext) {
    case 'link':
      modalTrigger = /*#__PURE__*/_jsx(LinkIcon, {
        onClick: onClickOpen,
        glyph: "share",
        placement: "before",
        glyphClassName: "margin-right-3 text-accent text-size-400",
        children: buttonText
      });
      break;
    case 'glyph':
      modalTrigger = /*#__PURE__*/_jsx(SaveGlyph, {
        action: "share",
        brand: brand,
        onClick: onClickOpen,
        uiContext: glyphColor === 'accent' ? 'accent-circle' : 'default',
        className: "text-size-600"
      });
      break;
    case 'stacked':
    case 'inline':
    default:
      modalTrigger = /*#__PURE__*/_jsx(IconButton, {
        glyph: "share",
        monochrome: glyphColor === 'default',
        label: buttonText,
        size: isInline ? 'small' : undefined,
        layout: isStacked ? 'stacked' : undefined,
        onClick: onClickOpen
      });
      break;
  }
  return /*#__PURE__*/_jsxs("div", {
    ...rest,
    children: [modalTrigger, /*#__PURE__*/_jsx(SideBarToModal, {
      title: title,
      content: content,
      collapsed: isMobile,
      show: isOpen,
      onHide: onClickClose,
      sideBarOptions: sideBarOptions,
      modalOptions: modalOptions
    })]
  });
}
export default /*#__PURE__*/React.memo(ShareContent);